import React, { useState, useEffect, useCallback, useRef } from 'react';

import clsx from 'clsx';
import { isAfter, differenceInDays } from 'date-fns';

import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import MenuIcon from '@material-ui/icons/Menu';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import CssBaseline from '@material-ui/core/CssBaseline';
import Drawer from '@material-ui/core/Drawer';
import Divider from '@material-ui/core/Divider';
import Avatar from '@material-ui/core/Avatar';
import List from '@material-ui/core/List';
import Modal from '@material-ui/core/Modal';
import Backdrop from '@material-ui/core/Backdrop';
import Fade from '@material-ui/core/Fade';
import ButtonBase from '@material-ui/core/ButtonBase';

import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import BlockIcon from '@material-ui/icons/Block';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { AiOutlinePoweroff } from 'react-icons/ai';
import { RiCloseLine } from 'react-icons/ri';

import { useAuth } from '../../hooks/auth';
import { useToast } from '../../hooks/toast';
import { useMonitor } from '../../hooks/monitor';
import { useMonitor as useNewMonitor } from '../../hooks/newMonitor';
import { useHeader } from '../../hooks/header';
import api from '../../services/api';
import MenuList from '../MenuList';
import LicenseModal from './LicenseModal';

import stylesMaterial from './stylesMaterial';
import {
  WarningButton,
  LogoutButton,
  ModalWrapper,
  CompanyInfo,
  DrawerBody,
  WhatsAppBox,
  WhatsAppIconWrapper,
} from './styles';
import IfoodService, { IIfoodStoreStatus } from '../../services/IfoodService';
import { Blob } from '../MenuList/styles';

const Header: React.FC = () => {
  const [isSmall, setIsSmall] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [openLicenseModal, setOpenLicenseModal] = useState(false);
  const [ifoodStoreStatus, setIfoodStoreStatus] = useState<IIfoodStoreStatus | null>(null);

  const {
    signOut,
    user,
    handleSetCaixaData,
    caixa,
    messageAlreadyView,
    setMessageAlreadyView,
    clearCaixaData,
  } = useAuth();
  const { addToast } = useToast();
  const { handleSetWasUpdated  } = useMonitor();
  const { emAguardo  } = useNewMonitor();
  
  const {
    whatsappStateLoja,
    whatsappStateGrupo,
    asideIsOpen,
    setAsideIsOpen,
    setWhatsappStateLoja,
    setWhatsappStateGrupo,
    setWhatsappNumberGrupo,
    setWhatsappNumberLoja,
  } = useHeader();
  const didMountRef = useRef(true);

  useEffect(() => {
    if (
      !messageAlreadyView &&
      user.data_expiracao &&
      differenceInDays(new Date(user.data_expiracao), new Date()) <= 10
    ) {
      if (didMountRef.current) {
        setMessageAlreadyView(true);
        setOpenLicenseModal(true);
      } else {
        didMountRef.current = false;
      }
    }
  }, [messageAlreadyView, user, setMessageAlreadyView]);

  const handleCloseCaixa = useCallback(async () => {
    try {
      await api.put(`/caixa/${caixa.id}`, {
        datafechamento: new Date(),
        saldofinal: caixa.saldo_final,
      });

      clearCaixaData();
      addToast({
        type: 'info',
        title: 'Loja Fechada',
      });
    } catch (err) {
      const { message } = (err as any).response.data;

      addToast({
        type: 'error',
        title: 'Operação negada',
        description: message,
      });
    }
  }, [addToast, caixa.id, caixa.saldo_final, clearCaixaData]);

  const handleOpenCaixa = useCallback(async () => {
    try {
      const response = await api.post('/caixa', {
        dataabertura: new Date(),
        saldoinicial: 0,
      });

      handleSetCaixaData({
        id: response.data.id,
        saldo_final: 0,
        isOpen: true,
      });
      addToast({
        type: 'success',
        title: 'Loja Aberta',
      });
    } catch (err) {
      const { message } = (err as any).response.data;

      addToast({
        type: 'error',
        title: 'Operação negada',
        description: message,
      });
    }
  }, [addToast, handleSetCaixaData]);

  useEffect(() => {
    if (window.screen.width <= 1200) {
      setAsideIsOpen(false);
      setIsSmall(true);
    } else {
      setAsideIsOpen(true);
      setIsSmall(false);
    }
  }, [setAsideIsOpen]);

  useEffect(() => {
    async function loadCaixaData(): Promise<void> {
      const caixaAberto = await api.post(`/caixa/${user.empresa_id}`);

      if (caixaAberto.data) {
        const { id } = caixaAberto.data;
        handleSetCaixaData({ id, saldo_final: 0, isOpen: true });
        handleSetWasUpdated(true);
      }
    }

    async function loadWhatsappState(): Promise<void> {
      const paramGrupo = await api.get('/parametro', {
        params: {
          descricao: 'whatsapp',
          empresa_id: user.empresa_id,
        },
      });

      const paramLoja = await api.get('/parametro/loja', {
        params: {
          descricao: 'whatsapp',
          empresa_id: user.empresa_id,
        },
      });

      setWhatsappNumberGrupo(
        paramGrupo?.data.role
          ? JSON.parse(paramGrupo.data.role).number
          : undefined,
      );

      setWhatsappNumberLoja(
        paramLoja.data.role
          ? JSON.parse(paramLoja.data.role).number
          : undefined,
      );

      if (paramGrupo.data && JSON.parse(paramGrupo?.data.role).status) {
        setWhatsappStateGrupo(true);
      } else {
        setWhatsappStateGrupo(false);
      }

      if (paramLoja.data && JSON.parse(paramLoja.data.role).status) {
        setWhatsappStateLoja(true);
      } else {
        setWhatsappStateLoja(false);
      }
    }

    loadCaixaData();
    loadWhatsappState();
  }, [
    user,
    setWhatsappStateLoja,
    setWhatsappNumberLoja,
    handleSetCaixaData,
    handleSetWasUpdated,
    setWhatsappNumberGrupo,
    setWhatsappStateGrupo,
  ]);

  useEffect(() => {
    let interval: number;

    IfoodService.getClientInfo(user.empresa_id).then((res) => {
      if(res && res.key) {
        IfoodService.getStoreStatus(user.empresa_id, res.key).then((response) => {
          setIfoodStoreStatus(response[0]);
        }).catch((error) => console.log(error));
        interval = setInterval(() => {
          if(res.key) {
            IfoodService.getStoreStatus(user.empresa_id, res.key).then((response) => {
              setIfoodStoreStatus(response[0]);
            }).catch((error) => console.log(error));
          }
        }, 30000);
      }
    }).catch((error) => console.log(error));

    return () => {
      clearInterval(interval);
    }
  }, [user])

  const classes = stylesMaterial();

  const handleDrawerOpen = (): void => {
    setAsideIsOpen(true);
  };

  const handleDrawerClose = (): void => {
    setAsideIsOpen(false);
  };

  const handleModalOpen = (): void => {
    setModalOpen(true);
  };

  const handleModalClose = (): void => {
    setModalOpen(false);
  };

  const handleOpenLicenseModal = (): void => {
    setOpenLicenseModal(true);
  };

  const handleCloseLicenseModal = (): void => {
    setOpenLicenseModal(false);
  };

  return (
    <>
      <CssBaseline />
      <AppBar
        elevation={0}
        position="absolute"
        className={clsx(
          classes.appBar,
          asideIsOpen && !isSmall && classes.appBarShift,
        )}
      >
        <Toolbar className={classes.toolbar}>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            className={clsx(
              classes.menuButton,
              asideIsOpen && classes.menuButtonHidden,
            )}
          >
            <MenuIcon />
            {emAguardo && <Blob style={{ margin: 0 }} />}
          </IconButton>

          <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
            <ButtonBase
              onClick={handleModalOpen}
              className={classes.openCloseStoreButton}
            >
              {caixa.isOpen ? (
                <CheckCircleIcon
                  style={{ color: '#35BA83', fontSize: '16px' }}
                />
              ) : (
                <BlockIcon style={{ color: '#F64444', fontSize: '16px' }} />
              )}
              <Typography className={classes.openCloseStoreText}>
                {caixa.isOpen ? 'Loja Aberta' : 'Loja Fechada'}
              </Typography>
            </ButtonBase>

            {!!ifoodStoreStatus && (
              ifoodStoreStatus.state === 'OK' || ifoodStoreStatus.state === 'WARNING' ? (
                <Typography className={classes.ifoodInfo}>
                  Ifood: Aberta
                </Typography>
              ) : (
                <Typography 
                  title={
                    ifoodStoreStatus.reopenable.reopenable 
                    ? ifoodStoreStatus.validations.filter((validation) => validation.state === 'ERROR')
                        .map((validation) => (
                      `${validation.message.title || ''}\n${validation.message.subtitle || ''}\n${validation.message.description || ''}`
                    )).join('/n')
                    : `${ifoodStoreStatus.message.title || ''}\n${ifoodStoreStatus.message.subtitle || ''}\n${ifoodStoreStatus.message.description || ''}`
                  }
                  className={classes.ifoodInfo}
                >
                  Ifood: Fechada
                </Typography>
              )
            )}
            
          </div>

          {/* eslint-disable-next-line no-nested-ternary */}
          {(user.data_expiracao
            ? isAfter(new Date(user.data_expiracao), new Date())
              ? differenceInDays(new Date(user.data_expiracao), new Date()) <= 8
              : false
            : false) && (
            <IconButton
              color="inherit"
              className={classes.licenseButtonWarning}
              onClick={handleOpenLicenseModal}
            >
              <WarningButton>
                <span
                  style={{ color: '#FF8946', fontSize: '20px' }}
                  className="material-icons"
                >
                  warning
                </span>
              </WarningButton>
            </IconButton>
          )}

          {(user.data_expiracao
            ? !isAfter(new Date(user.data_expiracao), new Date())
            : false) && (
            <IconButton
              color="inherit"
              className={classes.licenseButtonExpired}
              onClick={handleOpenLicenseModal}
            >
              <WarningButton>
                <span
                  style={{ color: '#F64444', fontSize: '20px' }}
                  className="material-icons"
                >
                  new_releases
                </span>
              </WarningButton>
            </IconButton>
          )}

          <IconButton
            color="inherit"
            onClick={signOut}
            className={classes.logoutButton}
          >
            <LogoutButton>
              <AiOutlinePoweroff size={20} />
            </LogoutButton>
          </IconButton>
        </Toolbar>
      </AppBar>

      <Modal
        className={classes.modal}
        open={modalOpen}
        onClose={handleModalClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={modalOpen}>
          <ModalWrapper>
            <div className="header">
              <h1>
                {caixa.isOpen
                  ? 'Deseja fechar a loja?'
                  : 'Deseja abrir a loja?'}
              </h1>

              <button type="button" onClick={handleModalClose}>
                <RiCloseLine size={30} />
              </button>
            </div>

            <div className="description">
              <span>
                Essa operação irá {caixa.isOpen ? 'fechar' : 'abrir'} sua loja.
                <br />
                Deseja confirmar?
              </span>
            </div>

            <div className="btnActions">
              <button
                type="button"
                className="confirm"
                onClick={() => {
                  caixa.isOpen ? handleCloseCaixa() : handleOpenCaixa();
                  handleModalClose();
                }}
              >
                Confirmar
              </button>

              <button
                type="button"
                className="cancel"
                onClick={handleModalClose}
              >
                Cancelar
              </button>
            </div>
          </ModalWrapper>
        </Fade>
      </Modal>

      <Drawer
        variant={isSmall ? 'temporary' : 'persistent'}
        onBackdropClick={handleDrawerClose}
        keepMounted
        classes={{
          paper: classes.drawerPaper,
        }}
        open={asideIsOpen}
        className={clsx(classes.drawer, !asideIsOpen && classes.drawerClosed)}
      >
        <IconButton onClick={handleDrawerClose} className={classes.toolbarIcon}>
          <ChevronLeftIcon />
        </IconButton>
        <CompanyInfo>
          <Avatar
            alt={user.empresa_nome_fantasia}
            src={`${process.env.REACT_APP_SERVER_URL}/files/${user.empresa_logo}`}
            className={classes.avatar}
          />
          <Typography
            component="h1"
            variant="h6"
            color="inherit"
            className={classes.companyName}
          >
            {user?.empresa_nome_fantasia?.toLocaleUpperCase()}
          </Typography>
        </CompanyInfo>

        <Divider className={classes.divider} />

        <DrawerBody>
          <List className={classes.list}>
            <MenuList />
          </List>

          <WhatsAppBox whatsappState={whatsappStateLoja || whatsappStateGrupo}>
            <WhatsAppIconWrapper
              whatsappState={whatsappStateLoja || whatsappStateGrupo}
            >
              <WhatsAppIcon color="inherit" style={{ width: 30, height: 30 }} />
            </WhatsAppIconWrapper>

            <Typography className={classes.whatsApp}>
              {whatsappStateLoja || whatsappStateGrupo
                ? 'HABILITADO'
                : 'DESABILITADO'}
            </Typography>
          </WhatsAppBox>
        </DrawerBody>
      </Drawer>
      {openLicenseModal ? (
        <LicenseModal
          handleCloseLicenseModal={handleCloseLicenseModal}
          openLicenseModal={openLicenseModal}
        />
      ) : (
        <div style={{ display: 'none' }} />
      )}
    </>
  );
};

export default Header;
